import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, User, Users } from 'lucide-react';

const RegisterPage = () => {
  const [step, setStep] = useState(1);
  const [role, setRole] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    sport: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const sports = [
    'Football', 'Basketball', 'Soccer', 'Baseball', 'Swimming',
    'Track & Field', 'Tennis', 'Volleyball', 'Wrestling', 'Gymnastics',
    'Martial Arts', 'CrossFit', 'General Fitness', 'Other'
  ];

  const handleRoleSelect = (selectedRole) => {
    setRole(selectedRole);
    setStep(2);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await register({
        ...formData,
        role: role,
      });
      toast.success('Welcome to Aura! Your system is ready.');
      // Redirect athletes to onboarding, supporters to their dashboard
      navigate(role === 'athlete' ? '/onboarding' : '/supporter', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    // Store selected role for after OAuth
    if (role) {
      localStorage.setItem('aura_pending_role', role);
    }
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-aura-bg flex flex-col">
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <button 
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/')}
          className="flex items-center gap-2 text-aura-text-muted hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="/aura-icon.png" 
            alt="Aura" 
            className="w-9 h-9 object-contain"
          />
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Step 1: Role Selection */}
          {step === 1 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white uppercase tracking-tight mb-2">
                  Join Aura Athlete Development
                </h1>
                <p className="text-aura-text-muted">
                  Aura is earned, not given. Choose your path.
                </p>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => handleRoleSelect('athlete')}
                  className="w-full aura-card p-6 text-left hover:border-aura-primary transition-all group"
                  data-testid="role-athlete-btn"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-aura-primary/20 flex items-center justify-center group-hover:bg-aura-primary/30 transition-colors">
                      <User className="w-6 h-6 text-aura-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wide mb-1">
                        I'm an Athlete
                      </h3>
                      <p className="text-aura-text-muted text-sm">
                        Build your training, nutrition, and recovery system. Own your development.
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleRoleSelect('supporter')}
                  className="w-full aura-card p-6 text-left hover:border-aura-secondary transition-all group"
                  data-testid="role-supporter-btn"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-aura-secondary/20 flex items-center justify-center group-hover:bg-aura-secondary/30 transition-colors">
                      <Users className="w-6 h-6 text-aura-secondary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wide mb-1">
                        I'm a Parent/Supporter
                      </h3>
                      <p className="text-aura-text-muted text-sm">
                        Support your athletes with visibility. Know when and where to help.
                      </p>
                    </div>
                  </div>
                </button>

                <Link
                  to="/organization/register"
                  className="block w-full aura-card p-6 text-left hover:border-aura-success transition-all group"
                  data-testid="role-organization-btn"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-aura-success/20 flex items-center justify-center group-hover:bg-aura-success/30 transition-colors">
                      <Users className="w-6 h-6 text-aura-success" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wide mb-1">
                        I'm a School/Club/Academy
                      </h3>
                      <p className="text-aura-text-muted text-sm">
                        Manage your team with roster tools. Choose org-funded or athlete-funded billing.
                      </p>
                    </div>
                  </div>
                </Link>
              </div>

              <p className="text-center text-aura-text-muted mt-6">
                Already have an account?{' '}
                <Link to="/login" className="text-aura-primary hover:text-aura-primary-hover font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          )}

          {/* Step 2: Registration Form */}
          {step === 2 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <div className={`w-12 h-12 mx-auto rounded-lg ${role === 'athlete' ? 'bg-aura-primary/20' : 'bg-aura-secondary/20'} flex items-center justify-center mb-4`}>
                  {role === 'athlete' ? (
                    <User className="w-6 h-6 text-aura-primary" />
                  ) : (
                    <Users className="w-6 h-6 text-aura-secondary" />
                  )}
                </div>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white uppercase tracking-tight mb-2">
                  Create Account
                </h1>
                <p className="text-aura-text-muted">
                  {role === 'athlete' ? 'Start earning your Aura' : 'Support your athletes'}
                </p>
              </div>

              {/* Google Register */}
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 border-white/20 text-white hover:bg-white/5 mb-6"
                onClick={handleGoogleRegister}
                data-testid="google-register-btn"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-aura-bg text-aura-text-subtle">or register with email</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your name"
                    required
                    className="h-12 bg-aura-surface border-white/10 text-white placeholder:text-aura-text-subtle focus:border-aura-primary focus:ring-aura-primary"
                    data-testid="name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="athlete@example.com"
                    required
                    className="h-12 bg-aura-surface border-white/10 text-white placeholder:text-aura-text-subtle focus:border-aura-primary focus:ring-aura-primary"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Create a strong password"
                      required
                      minLength={6}
                      className="h-12 bg-aura-surface border-white/10 text-white placeholder:text-aura-text-subtle focus:border-aura-primary focus:ring-aura-primary pr-12"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-aura-text-subtle hover:text-white transition-colors p-1"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {role === 'athlete' && (
                  <div className="space-y-2">
                    <Label htmlFor="sport" className="text-xs uppercase tracking-wider text-aura-text-subtle">
                      Primary Sport
                    </Label>
                    <Select value={formData.sport} onValueChange={(value) => setFormData({ ...formData, sport: value })}>
                      <SelectTrigger className="h-12 bg-aura-surface border-white/10 text-white" data-testid="sport-select">
                        <SelectValue placeholder="Select your sport" />
                      </SelectTrigger>
                      <SelectContent className="bg-aura-surface border-white/10">
                        {sports.map((sport) => (
                          <SelectItem key={sport} value={sport.toLowerCase()} className="text-white hover:bg-white/10">
                            {sport}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full h-12 text-white font-heading uppercase tracking-wider ${
                    role === 'athlete' 
                      ? 'bg-aura-primary hover:bg-aura-primary-hover' 
                      : 'bg-aura-secondary hover:bg-aura-secondary/90'
                  }`}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Start Free Trial'
                  )}
                </Button>

                <p className="text-center text-aura-text-subtle text-sm">
                  14-day free trial. No credit card required.
                </p>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
